import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@timely/ui';
import { supabase } from '../lib/supabaseClient';
import { useSession } from '../auth/SessionProvider';
import { safeReturnTo } from '../auth/session';
import { EMAIL_NOT_CONFIRMED, isEmailShaped, normaliseEmail, signInError } from '../auth/authErrors';
import { AuthLayout, AuthLink, Field, FormNotice } from './AuthLayout';

export function SignIn() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { state } = useSession();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    // Never honoured as given: `returnTo` comes from whoever wrote the link.
    const returnTo = safeReturnTo(params.get('returnTo'), '/');
    // Why the person is here, when an emailed link brought them (docs/product/phase-0/03 §2).
    const arrived = params.get('confirmed') === '1' ? 'Your email address is confirmed. Sign in with your password to continue.'
        : params.get('reset') === '1' ? 'Your password is changed. Sign in with the new one.'
            : params.get('again') === '1' ? 'For your security, sign in with your password to continue.'
                : null;

    if (state === 'active') return <Navigate to={returnTo} replace />;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setError(null);

        if (!isEmailShaped(email) || !password) {
            // Same sentence as a wrong password, on purpose: a form that
            // answers differently is a way of testing which addresses exist.
            setError(signInError(null));
            return;
        }

        setBusy(true);
        const { error: authError } = await supabase().auth.signInWithPassword({
            email: normaliseEmail(email),
            password,
        });
        setBusy(false);

        if (authError) {
            setError(signInError(authError));
            return;
        }
        navigate(returnTo, { replace: true });
    };

    return (
        <AuthLayout
            title="Sign in"
            lede="Your companies and everything in them."
            footer={<>New here? <AuthLink to="/signup">Create an account</AuthLink></>}
        >
            <form onSubmit={submit} noValidate>
                {error ? (
                    <FormNotice tone="error">
                        {error}
                        {error === EMAIL_NOT_CONFIRMED && <> <Link to="/verify-email" className="font-semibold underline underline-offset-2">Send a new link</Link></>}
                    </FormNotice>
                ) : arrived && <FormNotice tone="ok">{arrived}</FormNotice>}

                <Field large
                    label="Email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                />
                <Field large
                    label="Password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                />

                <div className="flex items-center justify-between gap-3 mt-6">
                    <AuthLink to="/forgot-password">Forgot your password?</AuthLink>
                    <Button type="submit" variant="primary" size="lg" busy={busy}>
                        {busy ? 'Signing in…' : 'Sign in'}
                    </Button>
                </div>
            </form>
        </AuthLayout>
    );
}
